import React from 'react';
import { Button } from './UIComponents';
import { ArrowRight, Shield, Clock, Eye, Activity, CheckCircle2, Zap, LayoutTemplate } from 'lucide-react';

interface LandingPageProps {
   onLogin: () => void;
   onSignup: () => void;
   onDemo?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onLogin, onSignup, onDemo }) => {
   return (
      <div className="min-h-screen bg-black text-zinc-200 font-sans selection:bg-zinc-800 selection:text-white overflow-x-hidden relative">

         {/* Background Effects */}
         <div className="fixed inset-0 z-0 pointer-events-none">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-zinc-800/20 blur-[120px]" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-900/10 blur-[120px]" />
         </div>

         {/* Nav */}
         <header className="fixed top-0 w-full px-6 py-4 flex justify-between items-center z-50 bg-black/60 backdrop-blur-xl border-b border-white/5">
            <div className="flex items-center gap-3">
               <div className="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center text-white border border-zinc-800 shadow-lg shadow-zinc-900/50">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                     <path d="M5 5L12 19L19 5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
               </div>
               <span className="font-semibold text-sm tracking-tight text-white">Verline</span>
            </div>
            <div className="flex gap-4">
               <button onClick={onLogin} className="text-sm font-medium text-zinc-400 hover:text-white transition-colors">Log in</button>
               <Button variant="primary" size="sm" onClick={onSignup} className="bg-white text-black hover:bg-zinc-200 border-0 hidden sm:flex">Get Started</Button>
            </div>
         </header>

         {/* Hero */}
         <section className="relative z-10 pt-32 md:pt-48 pb-20 md:pb-32 px-6 max-w-6xl mx-auto text-center flex flex-col items-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900/50 border border-zinc-800 text-[11px] font-medium text-zinc-400 mb-8 animate-fade-in">
               <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
               <span>Receiver Visibility Console 2.0 is live</span>
            </div>

            <h1 className="text-5xl md:text-8xl font-medium tracking-tighter mb-8 text-white leading-[1.1] md:leading-[1] bg-gradient-to-b from-white to-zinc-500 bg-clip-text text-transparent animate-fade-in">
               Build trust without <br /> the busy work.
            </h1>

            <p className="text-lg md:text-xl text-zinc-500 max-w-2xl mx-auto mb-12 font-normal leading-relaxed animate-fade-in delay-100 px-4">
               A dedicated status console for high-trust providers. Give receivers visibility into progress, timelines, and deliverables—without the constant emails.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 animate-fade-in delay-200 w-full sm:w-auto px-4">
               <Button size="lg" className="w-full sm:w-auto rounded-full px-8 h-12 text-base bg-white text-black hover:bg-zinc-200 border-0 shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] transition-transform hover:scale-105" onClick={onSignup}>
                  Get Started <ArrowRight size={18} className="ml-2" />
               </Button>
               {onDemo && (
                  <button onClick={onDemo} className="w-full sm:w-auto px-8 h-12 text-base text-zinc-400 hover:text-white transition-colors">
                     View Demo
                  </button>
               )}
            </div>

            {/* Hero Image / Graphic */}
            <div className="mt-20 w-full max-w-4xl relative animate-fade-in delay-300">
               <div className="absolute -inset-1 bg-gradient-to-b from-zinc-800 to-transparent opacity-20 blur-2xl rounded-2xl"></div>
               <div className="relative bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden shadow-2xl">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-900 bg-zinc-900/50">
                     <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-zinc-800"></div>
                        <div className="w-3 h-3 rounded-full bg-zinc-800"></div>
                        <div className="w-3 h-3 rounded-full bg-zinc-800"></div>
                     </div>
                  </div>
                  <div className="p-4 md:p-8 grid md:grid-cols-[240px_1fr] gap-8 text-left h-[300px] md:h-[400px] overflow-hidden">
                     {/* Fake Sidebar - hidden on mobile */}
                     <div className="space-y-6 hidden md:block">
                        <div className="space-y-2">
                           <div className="h-2 w-12 bg-zinc-800 rounded"></div>
                           <div className="h-8 w-full bg-zinc-900 rounded border border-zinc-800/50"></div>
                           <div className="h-8 w-full bg-transparent rounded"></div>
                        </div>
                        <div className="space-y-2">
                           <div className="h-2 w-16 bg-zinc-800 rounded"></div>
                           <div className="h-8 w-full bg-zinc-900/50 rounded"></div>
                           <div className="h-8 w-full bg-zinc-900/50 rounded"></div>
                           <div className="h-8 w-full bg-zinc-900/50 rounded"></div>
                        </div>
                     </div>
                     {/* Fake Content */}
                     <div className="space-y-6 w-full">
                        <div className="flex justify-between items-end pb-6 border-b border-zinc-900">
                           <div className="space-y-3 w-full">
                              <div className="h-8 w-2/3 md:w-64 bg-zinc-800 rounded"></div>
                              <div className="h-4 w-full md:w-96 bg-zinc-900 rounded"></div>
                           </div>
                           <div className="h-8 w-24 bg-zinc-800 rounded hidden md:block"></div>
                        </div>
                        <div className="space-y-4">
                           {[1, 2, 3].map(i => (
                              <div key={i} className="flex items-center gap-4 p-4 rounded-lg border border-zinc-800/50 bg-zinc-900/20">
                                 <div className={`w-4 h-4 rounded-full border ${i === 1 ? 'border-emerald-500 bg-emerald-500/20' : 'border-zinc-700'}`}></div>
                                 <div className="flex-1 h-3 bg-zinc-800 rounded w-1/3"></div>
                                 <div className="h-3 w-16 bg-zinc-900 rounded hidden sm:block"></div>
                              </div>
                           ))}
                        </div>
                     </div>
                  </div>
                  {/* Overlay Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent"></div>
               </div>
            </div>
         </section>

         {/* Features Grid */}
         <section className="relative z-10 py-20 md:py-32 px-6 border-t border-white/5 bg-zinc-950/30">
            <div className="max-w-6xl mx-auto">
               <div className="text-center mb-16 md:mb-20">
                  <h2 className="text-3xl md:text-5xl font-medium text-white mb-6 tracking-tight leading-tight">Everything they need. <br /> Nothing they don't.</h2>
                  <p className="text-zinc-500 text-lg max-w-2xl mx-auto">Verline strips away the noise of project management tools, leaving only the signal your receivers care about.</p>
               </div>

               <div className="grid md:grid-cols-3 gap-6">
                  <div className="group p-8 rounded-2xl bg-zinc-900/20 border border-white/5 hover:border-white/10 transition-all hover:bg-zinc-900/40">
                     <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center mb-6 text-zinc-400 group-hover:text-white group-hover:scale-110 transition-all duration-300 border border-white/5">
                        <LayoutTemplate size={24} strokeWidth={1.5} />
                     </div>
                     <h3 className="text-xl font-medium text-zinc-200 mb-3">The Single Source</h3>
                     <p className="text-zinc-500 leading-relaxed text-sm">
                        A dedicated URL for every project. No login required for read-only views, removing friction for stakeholders.
                     </p>
                  </div>

                  <div className="group p-8 rounded-2xl bg-zinc-900/20 border border-white/5 hover:border-white/10 transition-all hover:bg-zinc-900/40">
                     <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center mb-6 text-zinc-400 group-hover:text-white group-hover:scale-110 transition-all duration-300 border border-white/5">
                        <Clock size={24} strokeWidth={1.5} />
                     </div>
                     <h3 className="text-xl font-medium text-zinc-200 mb-3">Live Timeline</h3>
                     <p className="text-zinc-500 leading-relaxed text-sm">
                        Milestones update in real-time. Receivers can see exactly what stage the project is in and what's next.
                     </p>
                  </div>

                  <div className="group p-8 rounded-2xl bg-zinc-900/20 border border-white/5 hover:border-white/10 transition-all hover:bg-zinc-900/40">
                     <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center mb-6 text-zinc-400 group-hover:text-white group-hover:scale-110 transition-all duration-300 border border-white/5">
                        <Shield size={24} strokeWidth={1.5} />
                     </div>
                     <h3 className="text-xl font-medium text-zinc-200 mb-3">Controlled Access</h3>
                     <p className="text-zinc-500 leading-relaxed text-sm">
                        You decide what's visible. Keep internal drafts private and only publish milestones when they are ready.
                     </p>
                  </div>
               </div>
            </div>
         </section>

         {/* Comparison / Pain Point */}
         <section className="relative z-10 py-20 md:py-32 px-6 border-t border-white/5">
            <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 items-center">
               <div className="order-2 md:order-1 relative">
                  <div className="absolute inset-0 bg-rose-500/20 blur-[100px] rounded-full opacity-20"></div>
                  <div className="relative space-y-4 pl-4 md:pl-0">
                     <div className="bg-zinc-900 p-4 rounded-2xl rounded-tl-none border border-zinc-800 shadow-xl max-w-[280px] md:max-w-[300px] animate-fade-in">
                        <p className="text-sm text-zinc-300">Hey, any update on the designs?</p>
                        <span className="text-[10px] text-zinc-600 mt-2 block">Receiver • 9:41 AM</span>
                     </div>
                     <div className="bg-zinc-900 p-4 rounded-2xl rounded-tl-none border border-zinc-800 shadow-xl max-w-[280px] md:max-w-[300px] translate-x-4 md:translate-x-8 animate-fade-in delay-100">
                        <p className="text-sm text-zinc-300">Just checking in before my meeting...</p>
                        <span className="text-[10px] text-zinc-600 mt-2 block">Receiver • 10:15 AM</span>
                     </div>
                     <div className="bg-zinc-900 p-4 rounded-2xl rounded-tl-none border border-zinc-800 shadow-xl max-w-[280px] md:max-w-[300px] translate-x-8 md:translate-x-16 animate-fade-in delay-200">
                        <p className="text-sm text-zinc-300">Did you get my last email?</p>
                        <span className="text-[10px] text-zinc-600 mt-2 block">Receiver • 11:30 AM</span>
                     </div>
                  </div>
               </div>

               <div className="order-1 md:order-2">
                  <h3 className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-950/30 border border-rose-900/50 text-[11px] font-medium text-rose-400 mb-6">
                     <Zap size={12} fill="currentColor" />
                     <span>The Old Way</span>
                  </h3>
                  <h2 className="text-3xl md:text-4xl font-medium text-white mb-6 tracking-tight leading-tight">Stop managing <br /> receiver anxiety.</h2>
                  <p className="text-lg text-zinc-500 mb-8 leading-relaxed">
                     When receivers don't know where a project stands, they chase you. Verline preemptively answers "What's the status?" so you can focus on the work.
                  </p>
                  <ul className="space-y-3 mb-8">
                     {['Reduce update meetings by 80%', 'Eliminate "just checking in" emails', 'Look more professional than competitors'].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-zinc-400 text-sm">
                           <CheckCircle2 size={16} className="text-zinc-600" /> {item}
                        </li>
                     ))}
                  </ul>
               </div>
            </div>
         </section>

         {/* Footer */}
         <footer className="relative z-10 py-24 px-6 border-t border-white/5 bg-zinc-950 text-center">
            <div className="max-w-2xl mx-auto">
               <h2 className="text-3xl md:text-4xl font-medium text-white mb-8 tracking-tight">Ready to upgrade your workflow?</h2>
               <Button size="lg" className="rounded-full px-12 h-14 text-lg bg-white text-black hover:bg-zinc-200 border-0 shadow-lg shadow-zinc-900 w-full sm:w-auto" onClick={onSignup}>
                  Get Started Now
               </Button>
               <p className="mt-16 text-zinc-700 text-xs font-semibold tracking-widest uppercase">
                  Verline © {new Date().getFullYear()} • Status Infrastructure for Professionals
               </p>
            </div>
         </footer>
      </div>
   )
}